import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { variant as variantStyle } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';

import Text from '../Text';
import { useNotification } from './useNotification';
import { htmlSanitizer } from '../../lib/helpers';

const icons = {
  success: 'fa-regular fa-circle-check fa-xl',
  delete: 'fa-regular fa-trash-can fa-xl',
  error: 'fa-regular fa-triangle-exclamation fa-xl',
  default: 'fa-regular fa-messages fa-xl',
  language: 'fa-regular fa-messages fa-xl',
};

const ContentFactory = content => {
  if (typeof content === 'string') {
    return function Content() {
      return (
        <Text
          as="span"
          variant="small2"
          mt="10px"
          pr="8px"
          dangerouslySetInnerHTML={htmlSanitizer(content)}
        />
      );
    };
  }

  const Content = content;
  return Content;
};

const Notification = ({ id, content, title, variant, disappear }) => {
  const { handleClose } = useNotification();
  const ContentComponent = ContentFactory(content);

  useEffect(() => {
    let timer;
    if (disappear) {
      timer = setTimeout(() => handleClose(id), 3000);
    }

    return () => clearTimeout(timer);
  }, [handleClose, disappear, id]);

  return (
    <Wrapper
      variant={variant}
      bg="white"
      disappear={disappear}
      className="fadeIn"
      data-cy="notification-modal"
    >
      <IconSide variant={variant}>
        <Icon className={icons[variant]} />
      </IconSide>
      <Section data-id={id}>
        <CloseIcon onClick={() => handleClose(id)} />
        <Text as="span" variant="small1">
          {title}
        </Text>
        <ContentComponent handleClose={() => handleClose(id)} />
      </Section>
    </Wrapper>
  );
};

Notification.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  variant: PropTypes.string,
  disappear: PropTypes.bool,
};

export default Notification;

const fadeIn = keyframes`
  from {
    opacity:0;
  }

  to {
    opacity:1;
  }
`;

const Wrapper = styled.div`
  width: 325px;
  min-height: 100px;
  border-radius: 8px;
  box-shadow: 0px 4px 8px rgba(37, 40, 43, 0.14);
  position: relative;
  z-index: 999;
  display: flex;
  background-color: ${themeGet('colors.white')};
  margin-bottom: 10px;
  &.fadeIn {
    animation: ${fadeIn} 0.4s linear 1;
  }
  order: ${props => (props.disappear ? 1 : 0)};
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  padding: 16px;
  position: relative;
`;

const IconSide = styled.div`
  width: 48px;
  background-color: ${themeGet('colors.primary.100')};
  border-radius: 8px 0 0 8px;
  display: flex;
  align-items: center;
  ${variantStyle({
    variants: {
      success: {
        backgroundColor: 'success.default',
      },
      delete: {
        backgroundColor: 'error.default',
      },
      error: {
        backgroundColor: 'warning.default',
      },
      default: {
        backgroundColor: 'primary.100',
      },
    },
  })};
`;

const Icon = styled.i`
  display: flex;
  justify-content: center;
  flex: 1;
  color: ${themeGet('colors.white')};
`;

const CloseIcon = styled.i`
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
  color: ${themeGet('colors.black.100')};
  &:hover {
    color: ${themeGet('colors.black.40')};
  }
`;

CloseIcon.defaultProps = {
  className: 'fa-regular fa-xmark',
};
